/**
 * AI Assistant Storage
 * 
 * Client-side IndexedDB storage for chat conversations.
 * Conversations are stored locally - no server-side persistence needed.
 */

import { openDB, type DBSchema, type IDBPDatabase } from 'idb';

// ============================================================================
// TYPES
// ============================================================================

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: number;
}

export interface Conversation {
  id: string;
  context: 'dashboard' | { tenantId: string; incidentId: string };
  messages: ChatMessage[];
  createdAt: number;
  lastMessageAt: number;
}

interface AssistantDBSchema extends DBSchema {
  conversations: {
    key: string;
    value: Conversation;
    indexes: {
      'by-context': string;
      'by-date': number;
    };
  };
}

// ============================================================================
// DATABASE
// ============================================================================

const DB_NAME = 'alexein-assistant';
const DB_VERSION = 2;
const RETENTION_DAYS = 7;

let dbPromise: Promise<IDBPDatabase<AssistantDBSchema>> | null = null;

function getDb(): Promise<IDBPDatabase<AssistantDBSchema>> {
  if (!dbPromise) {
    dbPromise = openDB<AssistantDBSchema>(DB_NAME, DB_VERSION, {
      upgrade(db, oldVersion, _newVersion, transaction) {
        if (oldVersion < 1) {
          const store = db.createObjectStore('conversations', { keyPath: 'id' });
          store.createIndex('by-context', 'contextKey');
          store.createIndex('by-date', 'lastMessageAt');
        }
        if (oldVersion >= 1 && oldVersion < 2) {
          // v2: context keys are now user-scoped. Legacy un-scoped records
          // cannot be safely migrated, so purge them.
          transaction.objectStore('conversations').clear();
        }
      },
    });
  }
  return dbPromise;
}

// ============================================================================
// HELPERS
// ============================================================================

function contextToKey(context: Conversation['context'], userOid: string): string {
  if (context === 'dashboard') {
    return `user:${userOid}:dashboard`;
  }
  return `user:${userOid}:incident:${context.tenantId}:${context.incidentId}`;
}

function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

// ============================================================================
// CONVERSATION OPERATIONS
// ============================================================================

/**
 * Create a new conversation scoped to a specific user.
 */
export async function createConversation(
  context: Conversation['context'],
  userOid: string
): Promise<Conversation> {
  const db = await getDb();
  const now = Date.now();
  
  const conversation: Conversation & { contextKey: string } = {
    id: generateId(),
    context,
    contextKey: contextToKey(context, userOid),
    messages: [],
    createdAt: now,
    lastMessageAt: now,
  };
  
  await db.put('conversations', conversation);
  return conversation;
}

/**
 * Get a conversation by ID
 */
export async function getConversation(id: string): Promise<Conversation | undefined> {
  const db = await getDb();
  return db.get('conversations', id);
}

/**
 * Get the most recent conversation for a context + user, or create one.
 */
export async function getOrCreateConversation(
  context: Conversation['context'],
  userOid: string
): Promise<Conversation> {
  const db = await getDb();
  const contextKey = contextToKey(context, userOid);
  
  // Find existing conversation for this context + user
  const tx = db.transaction('conversations', 'readonly');
  const index = tx.store.index('by-context');
  const conversations: Conversation[] = [];
  
  for await (const cursor of index.iterate(contextKey)) {
    conversations.push(cursor.value);
  }
  
  // Return most recent, or create new
  if (conversations.length > 0) {
    conversations.sort((a, b) => b.lastMessageAt - a.lastMessageAt);
    return conversations[0];
  }
  
  return createConversation(context, userOid);
}

/**
 * Add a message to a conversation
 */
export async function addMessage(
  conversationId: string,
  role: 'user' | 'assistant',
  content: string
): Promise<ChatMessage> {
  const db = await getDb();
  const conversation = await db.get('conversations', conversationId);
  
  if (!conversation) {
    throw new Error(`Conversation ${conversationId} not found`);
  }
  
  const message: ChatMessage = {
    id: generateId(),
    role,
    content,
    timestamp: Date.now(),
  };
  
  conversation.messages.push(message);
  conversation.lastMessageAt = message.timestamp;
  
  await db.put('conversations', conversation);
  return message;
}

/**
 * Clear all messages in a conversation (keep the conversation)
 */
export async function clearConversation(conversationId: string): Promise<void> {
  const db = await getDb();
  const conversation = await db.get('conversations', conversationId);
  
  if (conversation) {
    conversation.messages = [];
    conversation.lastMessageAt = Date.now();
    await db.put('conversations', conversation);
  }
}

/**
 * Delete a conversation
 */
export async function deleteConversation(conversationId: string): Promise<void> {
  const db = await getDb();
  await db.delete('conversations', conversationId);
}

/**
 * Delete all conversations for a specific incident belonging to a user.
 */
export async function deleteIncidentConversations(
  tenantId: string,
  incidentId: string,
  userOid: string
): Promise<void> {
  const db = await getDb();
  const contextKey = `user:${userOid}:incident:${tenantId}:${incidentId}`;
  
  const tx = db.transaction('conversations', 'readwrite');
  const index = tx.store.index('by-context');
  
  for await (const cursor of index.iterate(contextKey)) {
    cursor.delete();
  }
}

/**
 * Clean up old conversations (older than RETENTION_DAYS)
 */
export async function cleanupOldConversations(): Promise<number> {
  const db = await getDb();
  const cutoff = Date.now() - RETENTION_DAYS * 24 * 60 * 60 * 1000;
  
  const tx = db.transaction('conversations', 'readwrite');
  const index = tx.store.index('by-date');
  
  let deleted = 0;
  for await (const cursor of index.iterate(IDBKeyRange.upperBound(cutoff))) {
    cursor.delete();
    deleted++;
  }
  
  return deleted;
}

/**
 * Clear all conversations (e.g., on logout)
 */
export async function clearAllConversations(): Promise<void> {
  const db = await getDb();
  await db.clear('conversations');
}

// ============================================================================
// INITIALIZATION
// ============================================================================

/**
 * Initialize storage and clean up old conversations
 * Call this on app startup
 */
export async function initAssistantStorage(): Promise<void> {
  try {
    const deleted = await cleanupOldConversations();
    if (deleted > 0) {
      console.log(`[AssistantStorage] Cleaned up ${deleted} old conversations`);
    }
  } catch (error) {
    console.warn('[AssistantStorage] Failed to initialize:', error);
  }
}
